// Hand-rolled typed i18n: a context provider + t(), with locale dictionaries
// lazy-imported as their own chunks (en ships in the main bundle and is the
// universal fallback). No library — ~300 UI strings and typed keys beat a
// runtime lookup table we'd have to lint ourselves.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { en, type MessageKey } from "./en";

export const SUPPORTED_LOCALES = [
  "en", "de", "nl", "sv", "pt", "es", "it", "sr", "uk", "id",
  "hr", "lt", "ro", "fr", "tl", "tr", "fi", "bs", "no", "pl",
] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = "locale";

/** Pure, testable: stored locale wins, then first base-tag match, then en. */
export function detectLocale(stored: string | null, navLangs: readonly string[]): Locale {
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) return stored as Locale;
  for (const l of navLangs) {
    const base = l.toLowerCase().split("-")[0];
    if ((SUPPORTED_LOCALES as readonly string[]).includes(base)) return base as Locale;
  }
  return "en";
}

/** {token} replacement; unknown tokens left intact. */
export function interpolate(s: string, params: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m));
}

type Dict = Record<MessageKey, string>;
const loaders: Record<Exclude<Locale, "en">, () => Promise<{ dict: Dict }>> = {
  de: () => import("./locales/de"), nl: () => import("./locales/nl"), sv: () => import("./locales/sv"),
  pt: () => import("./locales/pt"), es: () => import("./locales/es"), it: () => import("./locales/it"),
  sr: () => import("./locales/sr"), uk: () => import("./locales/uk"), id: () => import("./locales/id"),
  hr: () => import("./locales/hr"), lt: () => import("./locales/lt"), ro: () => import("./locales/ro"),
  fr: () => import("./locales/fr"), tl: () => import("./locales/tl"), tr: () => import("./locales/tr"),
  fi: () => import("./locales/fi"), bs: () => import("./locales/bs"), no: () => import("./locales/no"),
  pl: () => import("./locales/pl"),
};

interface I18n {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, params?: Record<string, string | number>) => string;
}

const Ctx = createContext<I18n>({ locale: "en", setLocale: () => {}, t: (k) => en[k] });

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    detectLocale(localStorage.getItem(STORAGE_KEY), navigator.languages ?? [navigator.language]),
  );
  const [dict, setDict] = useState<Dict | null>(null);

  useEffect(() => {
    document.documentElement.lang = locale;
    if (locale === "en") {
      setDict(null);
      return;
    }
    let cancelled = false;
    loaders[locale]().then(
      (m) => {
        if (!cancelled) setDict(m.dict);
      },
      (e) => {
        console.warn("locale load failed, staying on en", e);
        if (!cancelled) setDict(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLocaleState(l);
  }, []);

  const t = useCallback<I18n["t"]>(
    (key, params) => {
      const s = dict?.[key] ?? en[key];
      return params ? interpolate(s, params) : s;
    },
    [dict],
  );

  return <Ctx.Provider value={{ locale, setLocale, t }}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  return useContext(Ctx);
}

export function useT(): I18n["t"] {
  return useContext(Ctx).t;
}

/** Locale-aware date formatting bound to the active locale. */
export function useLocaleDate(): (ms: number, opts?: Intl.DateTimeFormatOptions) => string {
  const { locale } = useI18n();
  return useCallback((ms, opts) => new Intl.DateTimeFormat(locale, opts).format(new Date(ms)), [locale]);
}
